import React, { useContext } from "react";
import { WishListContext } from "../context/WishListProvider"; // Ensure path is correct
import { CartContext } from "../context/CartProvider"; // Ensure path is correct
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FaEye, FaCartPlus, FaHeartBroken, FaShoppingBag } from "react-icons/fa"; // Using FaCartPlus
import { FiTrash2, FiArrowRight } from "react-icons/fi"; // For delete and continue shopping

const WishListPage = () => {
  const { getWishList, removeWishList } = useContext(WishListContext);
  const { addToCart } = useContext(CartContext);
  const wishList = getWishList() || []; // Ensure wishList is always an array

  const primaryAccentColor = "#e5272e"; // Red from gradient
  const gradientColor = "linear-gradient(to right, #e5272e, #f59332)";

  const handleAddToCartAndRemove = (item) => {
    addToCart(item); // Add to cart
    removeWishList(item.product_id || item.id); // Remove from wishlist
    toast.success(`${item.product_name} moved to cart!`);
  };

  const handleRemoveFromWishlist = (itemId, itemName) => {
    removeWishList(itemId);
    toast.success(`${itemName} removed from wishlist.`);
  };
  
  // Helper to get the correct price, prioritizing selectedAttributes
  const getDisplayPrice = (item) => {
    const regularPrice = parseFloat(item.selectedAttributes?.price || item.reguler_price || item.price || 0);
    const discountPrice = parseFloat(item.selectedAttributes?.discount_price || item.discount_price || regularPrice);
    return { regularPrice, discountPrice };
  };


  return (
    <>
      <style>
        {`
          .wishlist-page-container {
            background-color: #f8f9fa; /* Light gray background */
            min-height: 80vh; /* Ensure it takes good height */
          }
          .wishlist-title {
            color: ${primaryAccentColor};
            font-weight: 700;
            font-size: 2.5rem; /* Larger title */
          }
          .wishlist-empty-card {
            background-color: #fff;
            border-radius: 12px;
            box-shadow: 0 6px 20px rgba(0,0,0,0.07);
            padding: 3rem 2rem;
          }
          .wishlist-empty-card svg { /* Icon styling */
            color: ${primaryAccentColor};
            opacity: 0.8;
          }
          .btn-start-shopping {
            background-image: ${gradientColor};
            color: white;
            border: none;
            padding: 0.75rem 1.8rem;
            border-radius: 50px; /* Pill shape */
            font-weight: 600;
            font-size: 1rem;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
          }
          .btn-start-shopping:hover {
            opacity: 0.9;
            color: white;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .wishlist-item-card {
            border-radius: 12px;
            border: 1px solid #e9ecef; /* Softer border */
            transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
            background-color: #fff;
          }
          .wishlist-item-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          }
          .wishlist-item-card .card-img-top {
            height: 220px; /* Consistent image height */
            object-fit: cover; /* Changed to cover for better aesthetics */
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
            padding: 10px; /* Added padding around image for 'contain' if preferred */
            object-fit: contain; /* Reverted to contain as per original code */
          }
          .wishlist-item-card .card-body {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 1.25rem;
          }
          .wishlist-item-card .card-title {
            color: #212529; /* Darker title */
            font-weight: 600;
            font-size: 1.1rem;
            margin-bottom: 0.5rem;
            line-height: 1.3;
            min-height: 50px; /* Ensure space for 2 lines of title */
          }
          .wishlist-price-section .original-price {
            color: #6c757d;
            font-size: 0.9rem;
          }
          .wishlist-price-section .discounted-price {
            color: #28a745; /* Green for discounted price */
            font-size: 1.15rem;
            font-weight: 700;
          }
          .wishlist-actions .btn {
            font-size: 0.85rem;
            padding: 0.5rem 0.8rem;
            font-weight: 500;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.4rem; /* Gap between icon and text */
            flex-grow: 1; /* Make buttons take equal space in btn-group */
          }
          .btn-details-wishlist {
            border-color: ${primaryAccentColor};
            color: ${primaryAccentColor};
          }
          .btn-details-wishlist:hover {
            background-color: ${primaryAccentColor};
            color: white;
          }
          .btn-addtocart-wishlist {
            background-image: ${gradientColor};
            color: white;
            border: none;
          }
          .btn-addtocart-wishlist:hover {
            opacity: 0.9;
            color: white;
          }
          .btn-remove-wishlist {
            border-color: #dc3545;
            color: #dc3545;
          }
          .btn-remove-wishlist:hover {
            background-color: #dc3545;
            color: white;
          }
        `}
      </style>
      <div className="container py-5 wishlist-page-container">
        <h1 className="text-center mb-5 wishlist-title">My Wishlist</h1>
        {wishList.length === 0 ? (
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="text-center wishlist-empty-card">
                <FaHeartBroken size={70} className="mb-4" />
                <h4 className="mb-3">Your Wishlist is Currently Empty</h4>
                <p className="text-muted mb-4">
                  Add items you love to your wishlist to keep track of them. <br/> Start exploring our collection now!
                </p>
                <Link to="/shop" className="btn btn-start-shopping">
                  <FaShoppingBag size={18} style={{ marginRight: '8px' }} />
                  Start Shopping
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {wishList.map((item) => {
              const { regularPrice, discountPrice } = getDisplayPrice(item);
              const hasDiscount = discountPrice < regularPrice;

              return (
                <div className="col-sm-6 col-md-4 col-lg-3" key={item.product_id || item.id}> {/* Adjusted columns for more items per row */}
                  <div className="card h-100 wishlist-item-card">
                    <Link to={`/product_details/${item.product_id || item.id}`} className="text-decoration-none">
                      <img
                        src={item.productImage || 'https://placehold.co/300x220/E9ECEF/333?text=Product'}
                        className="card-img-top"
                        alt={item.product_name}
                        onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/300x220/E9ECEF/333?text=No+Image"; }}
                      />
                    </Link>
                    <div className="card-body">
                      <div>
                        <h5 className="card-title">
                           <Link to={`/product_details/${item.product_id || item.id}`} className="text-decoration-none text-dark">
                            {item.product_name}
                           </Link>
                        </h5>
                        <div className="mb-3 wishlist-price-section">
                          {hasDiscount && (
                            <span className="original-price text-decoration-line-through me-2">
                              £{regularPrice.toFixed(2)}
                            </span>
                          )}
                          <span className="discounted-price">
                            £{discountPrice.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className="btn-group d-flex gap-2 mt-auto wishlist-actions">
                        <Link
                          to={`/product_details/${item.product_id || item.id}`}
                          className="btn btn-outline-primary btn-details-wishlist"
                          title="View Details"
                        >
                          <FaEye /> Details
                        </Link>
                        <button
                          onClick={() => handleAddToCartAndRemove(item)}
                          className="btn btn-addtocart-wishlist"
                          title="Add to Cart & Remove from Wishlist"
                        >
                          <FaCartPlus /> Cart
                        </button>
                        <button
                          className="btn btn-outline-danger btn-remove-wishlist"
                          onClick={() => handleRemoveFromWishlist(item.product_id || item.id, item.product_name)}
                          title="Remove from Wishlist"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default WishListPage;
